import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CloseIcon, IconButton, Input } from "../../ui/index.js";
import { MAX_SERVICES } from "./sources.js";
import type { DraftService } from "./types.js";

/** Строка черновика услуг: название, цена в RSD, длительность, пометка «проверьте» от разбора источников. */
export interface ServiceRow {
  name: string;
  price: string;
  minutes: string;
  check: boolean;
}

export const digits = (s: string) => s.replace(/\D/g, "");
export const toRow = (d: DraftService): ServiceRow => ({ name: d.name, price: String(d.priceRsd), minutes: String(d.durationMin), check: Boolean(d.check) });
export const rowValid = (r: ServiceRow) =>
  r.name.trim().length > 0 && digits(r.price) !== "" && Number(digits(r.minutes)) >= 5 && Number(digits(r.minutes)) <= 600;
export const toDraft = (r: ServiceRow): DraftService => ({
  name: r.name.trim(),
  priceRsd: Number(digits(r.price)),
  durationMin: Number(digits(r.minutes)),
  ...(r.check ? { check: true } : {}),
});

/** Новые позиции из разбора добавляются в конец; с тем же названием заменяют старую строку. */
export function mergeRows(rows: ServiceRow[], parsed: DraftService[]): ServiceRow[] {
  const out = [...rows];
  for (const p of parsed) {
    const i = out.findIndex((r) => r.name.trim().toLowerCase() === p.name.trim().toLowerCase());
    if (i >= 0) out[i] = toRow(p);
    else out.push(toRow(p));
  }
  return out;
}

/**
 * Список строк услуг с правкой на месте: шаг «Услуги» мастера настройки и блок «Заполнить из источника» на странице услуг.
 * Правка строки снимает пометку «проверьте».
 */
export function ServiceRowsEditor({ rows, onChange, canAdd = true }: { rows: ServiceRow[]; onChange: (rows: ServiceRow[]) => void; canAdd?: boolean }) {
  const { t } = useTranslation();
  const setRow = (i: number, patch: Partial<ServiceRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch, check: false } : r)));
  return (
    <>
      <div className="space-y-3">
        {rows.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки черновика без id, порядок меняется только удалением
          <Card key={i} tone={r.check ? "warning" : "default"} className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={r.name}
                onChange={(e) => setRow(i, { name: e.target.value })}
                aria-label={t("onb.services.name")}
                placeholder={t("onb.services.name")}
                className="py-2"
              />
              <IconButton label={t("common.remove")} icon={<CloseIcon />} onClick={() => onChange(rows.filter((_, j) => j !== i))} />
            </div>
            <div className="flex items-center gap-2">
              <Input
                inputMode="numeric"
                value={r.price}
                onChange={(e) => setRow(i, { price: digits(e.target.value) })}
                aria-label={t("onb.services.price")}
                className="py-2"
              />
              <span className="text-sm text-ink-3">RSD</span>
              <Input
                inputMode="numeric"
                value={r.minutes}
                onChange={(e) => setRow(i, { minutes: digits(e.target.value) })}
                aria-label={t("onb.services.minutes")}
                className="py-2"
              />
              <span className="text-sm text-ink-3">{t("onb.services.min")}</span>
            </div>
            {r.check && <Badge tone="amber">{t("onb.services.check")}</Badge>}
          </Card>
        ))}
      </div>
      {canAdd && (
        <Button
          variant="ghost"
          className="mt-2 w-full"
          disabled={rows.length >= MAX_SERVICES}
          onClick={() => onChange([...rows, { name: "", price: "", minutes: "30", check: false }])}
        >
          + {t("onb.services.add")}
        </Button>
      )}
    </>
  );
}
